import type { AttendanceDetail, AuditEntry } from '@parinaam-vms/shared';
import { apiGet, apiGetPage, apiPost, toQueryString } from './client';
import type { Page } from './client';

/** A `type`, not an `interface`, so it can be passed to `toQueryString`. */
export type AttendanceListQuery = {
  volunteer_id?: string;
  activity_id?: string[];
  /** The API has filtered on this since D19; the portal started using it 9 Oct 2026. */
  session_id?: string[];
  program_id?: string[];
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
};

export interface CreateAttendanceBody {
  volunteer_id: string;
  session_id: string;
  attendance_date: string;
  /** Minutes — the stored unit (§1.13). `DurationInput` does the conversion. */
  duration_minutes: number;
  notes?: string | null;
  /**
   * Plan D3. Sent only on the retry after the API has answered
   * `ASSIGNMENT_REQUIRED` and staff have confirmed. Never sent on a first
   * attempt — the point of the flow is that the warning is seen.
   */
  allow_unassigned?: boolean;
  /**
   * Plan D4. Sent only on the retry after `OVERLAP_DETECTED`. Independent of
   * `allow_unassigned`: one record can need both confirmations, and answering
   * one question must not quietly answer the other.
   */
  allow_overlap?: boolean;
}

export function fetchAttendance(params: AttendanceListQuery): Promise<Page<AttendanceDetail>> {
  return apiGetPage<AttendanceDetail>(`/attendance${toQueryString(params)}`);
}

export function fetchActivityAttendance(activityId: string): Promise<AttendanceDetail[]> {
  return apiGet<AttendanceDetail[]>(`/activities/${activityId}/attendance`);
}

export function createAttendance(body: CreateAttendanceBody): Promise<AttendanceDetail> {
  return apiPost<AttendanceDetail>('/attendance', body);
}

/** Every change to one attendance record, newest first (M2, §§7.20, 11.53). */
export function fetchAttendanceHistory(id: string): Promise<AuditEntry[]> {
  return apiGet<AuditEntry[]>(`/attendance/${id}/history`);
}

/*
 * `approveAttendance` and `rejectAttendance` called two endpoints that were
 * removed on 8 Oct 2026 with the approval workflow. A recorded row is approved
 * as it is written, so there is no decision left to send.
 */

/**
 * Fix what was observed — M2, §1.15.
 *
 * An absent field is left alone; the status is never touched, so an approved
 * record stays approved with corrected hours.
 */
export interface CorrectAttendanceBody {
  duration_minutes?: number;
  attendance_date?: string;
  notes?: string | null;
  reason: string;
}

export function correctAttendance(
  id: string,
  body: CorrectAttendanceBody
): Promise<AttendanceDetail> {
  return apiPost<AttendanceDetail>(`/attendance/${id}/correction`, body);
}

/**
 * Deliberately change the hours, and flag the record — M2, §§1.16, 4.28.
 *
 * Different from a correction: this one says the figure was right and is being
 * changed anyway, which is why §5.42 wants it findable afterwards.
 */
export function overrideAttendance(
  id: string,
  durationMinutes: number,
  reason: string
): Promise<AttendanceDetail> {
  return apiPost<AttendanceDetail>(`/attendance/${id}/override`, {
    duration_minutes: durationMinutes,
    reason,
  });
}

/**
 * The signed-in volunteer's own hours.
 *
 * Scoped to the account: there is no id in the path, so it cannot be pointed at
 * anybody else.
 */
export function listMyAttendance(): Promise<AttendanceDetail[]> {
  return apiGet<AttendanceDetail[]>('/me/attendance');
}
